import { Inject, Injectable } from '@angular/core';

import { CachableData } from '@app/models/cacheable-data';
import isAfter from 'date-fns/esm/isAfter';
import isBefore from 'date-fns/esm/isBefore';
import addMonths from 'date-fns/esm/addMonths';
import { AuthService } from './auth.service';
import { WINDOW } from '@app/common/app-tokens';

@Injectable({
  providedIn: 'root'
})
export class CacheService {

  // The cache is created to work with the approach of fetching any local data first and showing that to the
  // User while fetching the real data in background and updating the cache and data when the network request is complete.
  private persistentCache = localStorage;
  private sessionCache = sessionStorage;
  private readonly cachePrefix = 'rise-pdp';

  constructor(private authService: AuthService, @Inject(WINDOW) private window: Window) {

    function storageMockFunc() {
      const storage = {};

      return {
        setItem: function(key, value) {
          storage[key] = value || '';
        },
        getItem: function(key) {
          return key in storage ? storage[key] : null;
        },
        removeItem: function(key) {
          delete storage[key];
        },
        clear: function() {
          for (const key in storage) {
              if (storage.hasOwnProperty(key)) {
                  delete storage[key];
              }
          }
        },
        get length() {
          return Object.keys(storage).length;
        },
        key: function(i) {
          const keys = Object.keys(storage);
          return keys[i] || null;
        }
      };
    }

    (<any>window).storageMock = storageMockFunc();
    this.persistentCache = (<any>window).storageMock;

  }

  private generateCacheKey(cacheKey: string) {
    if (this.authService.accountInfo) {
      if (this.authService.accountInfo.username) {
        return `${this.cachePrefix}-${this.authService.accountInfo.username}-${cacheKey}`;
      }
    }
    return null;
  }

  public get<T>(cacheKey: string): T {
    try {
      const generatedKey = this.generateCacheKey(cacheKey);
      if (generatedKey) {
        const cachedItem = this.persistentCache.getItem(generatedKey);
        if (cachedItem) {
          const cachableData = JSON.parse(cachedItem) as CachableData<T>;
          if (cachableData.AbsoluteExpirationDate) {
            const expirationDate = new Date(cachableData.AbsoluteExpirationDate);
            if (isAfter(expirationDate, new Date())) {
              return cachableData.Data;
            } else {
              this.removeFromPersistentCache(cacheKey);
              return null;
            }
          }
          return cachableData.Data;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  public getItemAsStringFromSessionCache(key: string): string {
    return this.sessionCache.getItem(key);
  }

  public set<T>(cacheKey: string, value: T, absoluteExpirationDate?: Date) {
    const generatedKey = this.generateCacheKey(cacheKey);

    if (!absoluteExpirationDate) {
      absoluteExpirationDate = addMonths(new Date(), 3);
    }

    if (generatedKey) {
      this.persistentCache.setItem(generatedKey, JSON.stringify(
        new CachableData({
          Data: value,
          AddedToCache: new Date(),
          AbsoluteExpirationDate: absoluteExpirationDate ? absoluteExpirationDate : null
        })));
    }
  }

  public setWithCustomKey<T>(cacheKey: string, value: T) {
    const generatedKey = `${this.cachePrefix}-${cacheKey}`;
    this.sessionCache.setItem(generatedKey, JSON.stringify(
      new CachableData({
        Data: value,
        AddedToCache: new Date(),
      })));
  }

  public getWithCustomKey<T>(cacheKey: string) {
    const generatedKey = `${this.cachePrefix}-${cacheKey}`;
    const cachedItem = this.sessionCache.getItem(generatedKey);
    if (cachedItem) {
      return (JSON.parse(cachedItem) as CachableData<T>).Data;
    }
    return null;
  }

  public removeFromSessionCache(cacheKey: string) {
    const generatedKey = `${this.cachePrefix}-${cacheKey}`;
    if (generatedKey) {
      this.sessionCache.removeItem(generatedKey);
    }
  }

  public removeFromPersistentCache(cacheKey: string) {
    const generatedKey = this.generateCacheKey(cacheKey);
    if (generatedKey) {
      this.persistentCache.removeItem(generatedKey);
    }
  }

  public removeAllExpiredItemsInPersistentCache() {
    for (let i = 0; i < this.persistentCache.length; i++) {
      const key = this.persistentCache.key(i);
      const cachedItem = JSON.parse(this.persistentCache.getItem(key)) as CachableData<any>;
      if (cachedItem) {
        if (cachedItem.AbsoluteExpirationDate) {
          const expirationDate = new Date(cachedItem.AbsoluteExpirationDate);
          if (isBefore(expirationDate, new Date())) {
            this.removeFromPersistentCache(key);
          }
        }
      }
    }
  }

  public removeAllitemsInPersistentCacheWithPrefix(cacheKey: string) {
    const generatedKey = this.generateCacheKey(cacheKey);
    if (generatedKey) {
      const tmpArr = []; // Array to hold the keys
      // Iterate over localStorage and insert the keys that meet the condition into arr
      for (let i = 0; i < this.persistentCache.length; i++) {
        if (this.persistentCache.key(i).indexOf(generatedKey) !== -1) {
          tmpArr.push(this.persistentCache.key(i));
        }
      }
      // Iterate over arr and remove the items by key
      for (let i = 0; i < tmpArr.length; i++) {
        this.persistentCache.removeItem(tmpArr[i]);
      }
    }
  }

  public clearPersistentCache() {
    this.persistentCache.clear();
  }

}
